import React, { useEffect, useState } from "react";
// XLSX
import * as XLSX from "xlsx-js-style";
// ANT DESIGN COMPONENTS
import { Table, Tag, Button, Tooltip, InputNumber, Upload, Modal } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash,
  faCircleCheck,
  faXmarkCircle,
  faDownload,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector } from "react-redux";
import { usePutAdapterMutation } from "../../../../redux/adapters/adapterAPI";
// AUTHORIZATION
import GETJwtToken from "../../../../redux/authentication/authentication";
// INTERFACES
import { RootState } from "../../../../app/store";
import { KeysAdapterOffsetsTableInterface } from "../../../../interfaces/Key.interface";
import { MessageTypeInterface } from "../../../../interfaces/MessageType.interface";
import { AdapterInterface } from "../../../../interfaces/Adapter.interface";
// CUSTOM HOOKS
import useGetAllMessagesTypes from "../../../../customHooks/GET_requests/useGetAllMessagesTypes";
// COMPONENTS
import TableNoDataBox from "../../../components/table/TableNoDataBox";
import GlobalAlert2 from "../../../home/GlobalAlert2";
import ButtonSquareSave from "../../../components/buttons/ButtonSquareSave";
import TableColumnLiftType from "../../../components/table/TableColumnLiftType";
import KeysConfirmSaveTableData from "../../keys/KeysConfirmSaveTableData";
// UTILS
import { buildOffsetsTable, buildPreviousOffsets, deriveUnitCount } from "../../keys/utils";
import { getTableRowClass, generateExcelAndDownloadDefinitions } from "../../../../utils/utils";
// FLATBUFFER DISPLAY HELPERS
import { formatUuidT } from "../../../../flatbuffers/display";

export default function OffsetTable({
  adapter,
  isLoading,
}: {
  adapter: AdapterInterface;
  isLoading: boolean;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const { Column } = Table;
  const [t] = useTranslation("global");
  const parsedOffsetMapping: any[] = (() => {
    try {
      const raw = adapter.offsetMappingJson;
      if (!raw) return [];
      return typeof raw === "string" ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  })();
  const unitCount = deriveUnitCount(parsedOffsetMapping);

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [data, setData] = useState<KeysAdapterOffsetsTableInterface[]>([]);
  const [openModalConfirmSave, setOpenModalConfirmSave] = useState<boolean>(false);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const {
    messagesTypes,
    isLoadingGetAllMessagesTypes,
    errorMessageGetAllMessagesTypes,
    isErrorGetAllMessagesTypes,
    totalRowsGetAllMessagesTypes,
  } = useGetAllMessagesTypes();

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [
    triggerPutAdapter,
    {
      isLoading: isLoadingPutAdapter,
      isSuccess: isSuccessPutAdapter,
      isError: isErrorPutAdapter,
      error: errorPutAdapter,
    },
  ] = usePutAdapterMutation();

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const buildFirstData = (messageTypes: MessageTypeInterface[], units: number) => {
    const offsetsTable = buildOffsetsTable(messageTypes, units);
    const fillPreviousOffsets = buildPreviousOffsets(offsetsTable, parsedOffsetMapping);
    setData(fillPreviousOffsets);
  };

  const onClickDelete = async (record: KeysAdapterOffsetsTableInterface) => {
    const copy = structuredClone(data);
    const index = copy.findIndex((x: KeysAdapterOffsetsTableInterface) => x.id === record.id);
    if (index >= 0) {
      for (let i = 0; i < unitCount; i += 1) {
        const key = `car_${i}`;
        copy[index][key] = null;
      }
      copy[index].isValid = false;
    }
    setData(copy);
  };

  const onChangeInputNumber = (newValue: number, id: string, name: string) => {
    const copy = structuredClone(data);
    const index = copy.findIndex((x: KeysAdapterOffsetsTableInterface) => x.id === id);
    if (index >= 0) {
      copy[index][name] = newValue || 0;
      let hasPositiveValue = false;
      for (let i = 0; i < unitCount; i += 1) {
        const key = `car_${i}`;
        if (copy[index][key] >= 0) {
          hasPositiveValue = true;
          break;
        }
      }
      copy[index].isValid = hasPositiveValue;
    }
    setData(copy);
  };

  const buildAdapterOffsets = () => {
    const transformed: any = [];
    data.forEach((item: KeysAdapterOffsetsTableInterface) => {
      Object.keys(item).forEach((key) => {
        if (key.startsWith("car_") && item[key] !== null) {
          if (
            item &&
            item.subType &&
            item.subType !== "" &&
            item.type.toUpperCase() !== item.subType.toUpperCase()
          ) {
            transformed.push({
              message_type: item.idMessageType,
              offset: item[key],
              unit_number: parseInt(key.split("_")[1], 10),
              message_sub_type: item.subType,
            });
          } else {
            transformed.push({
              message_type: item.idMessageType,
              offset: item[key],
              unit_number: parseInt(key.split("_")[1], 10),
              message_sub_type: item.subType,
            });
          }
        }
      });
    });
    return transformed;
  };

  const onClickSave = async () => {
    const adapterOffsets = buildAdapterOffsets();
    const token = await GETJwtToken();
    const copy = structuredClone(adapter);
    copy.offsetMappingJson = JSON.stringify(adapterOffsets);
    triggerPutAdapter({ id: adapter.id, token, body: copy });
  };

  const buildUploadData = (jsonData: KeysAdapterOffsetsTableInterface[]) => {
    const newData = jsonData.map((row) => {
      const isValid = Array.from({ length: unitCount }).some((_, i) => {
        const key = `car_${i}`;
        const value = row[key];
        return value !== null && value !== undefined && value >= 0;
      });
      return {
        ...row,
        isValid,
      };
    });
    setData(newData);
  };

  const handleUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const binaryStr = e.target?.result;
        const workbook = XLSX.read(binaryStr, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData: KeysAdapterOffsetsTableInterface[] = XLSX.utils.sheet_to_json(sheet);
        buildUploadData(jsonData);
      } catch (error2) {
        // eslint-disable-next-line no-console
        console.error("Error reading the Excel file. Please try again.");
      }
    };

    reader.readAsBinaryString(file); // Read the file as binary
    return false; // Prevent default upload behavior
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if (messagesTypes) {
      buildFirstData(messagesTypes, unitCount);
    }
  }, [messagesTypes]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      <div
        className="generalStyles__pageHeader"
        style={{ display: "flex", justifyContent: "space-between" }}
      >
        <div>
          <div className="generalStyles__flex">
            <TableColumnLiftType liftType={adapter.liftType} />
            <Tag color="orange">{adapter.protocolType}</Tag>
            <Tag color="cyan">{String(adapter.name ?? "--")}</Tag>
            <Tag color="geekblue">OFFSETS</Tag>
          </div>
        </div>
        <div className="generalStyles__flex ">
          <div>
            <Button
              className="buttonStyle__13 generalStyles__mlFix"
              style={{ marginTop: "-5px" }}
              onClick={() => {
                generateExcelAndDownloadDefinitions(data, String(adapter.name ?? ""));
              }}
              loading={isLoading}
              icon={<FontAwesomeIcon className="" icon={faDownload} />}
            />
          </div>
          <div>
            <Upload accept=".xlsx, .xls" beforeUpload={handleUpload} showUploadList={false}>
              <Button
                className="buttonStyle__20  generalStyles__mlFix buttonStyle__normal"
                style={{ marginTop: "-5px" }}
                icon={<FontAwesomeIcon icon={faUpload} />}
              />
            </Upload>
          </div>
          <div>
            <ButtonSquareSave
              onClick={() => {
                setOpenModalConfirmSave(true);
              }}
              disabledButton={isLoading || isLoadingGetAllMessagesTypes || isLoadingPutAdapter}
              isLoading={isLoading || isLoadingGetAllMessagesTypes || isLoadingPutAdapter}
            />
          </div>
        </div>
      </div>
      <Table
        locale={{
          emptyText: (
            <>
              {isErrorGetAllMessagesTypes ? (
                <TableNoDataBox
                  isError={isErrorGetAllMessagesTypes}
                  errorMessage={errorMessageGetAllMessagesTypes}
                  noItemsFound="No actions found"
                />
              ) : (
                <div className="generalStyles__flexCenter" style={{ height: "300px" }}>
                  <div>
                    <p>{t("general.offsetsNotFound")}</p>
                  </div>
                </div>
              )}
            </>
          ),
        }}
        rowClassName={(record, index) => getTableRowClass(index, theme)}
        dataSource={data}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ["10", "25", "50", "100"],
        }}
        rowKey={(record: any) => formatUuidT(record.id)}
        size="small"
        className="mt-3"
        loading={isLoadingGetAllMessagesTypes || isLoading || isLoadingPutAdapter}
        footer={() => (
          <div className="generalStyles__flexEnd">
            <Tag>{totalRowsGetAllMessagesTypes} Rows</Tag>
          </div>
        )}
      >
        <Column
          title={t("general.state")}
          dataIndex="state"
          key="state"
          align="center"
          render={(text: string, record: KeysAdapterOffsetsTableInterface) => (
            <>
              {record.isValid ? (
                <FontAwesomeIcon className="generalStyles__success" icon={faCircleCheck} />
              ) : (
                <Tooltip title={t("general.keyDuplicated")}>
                  <FontAwesomeIcon className="generalStyles__error" icon={faXmarkCircle} />
                </Tooltip>
              )}
            </>
          )}
        />
        <Column
          title={t("general.type")}
          dataIndex="type"
          key="type"
          render={(text) => <>{text}</>}
        />
        <Column
          title={t("general.subType")}
          dataIndex="subType"
          key="subType"
          render={(text) => <>{text || "NA"}</>}
        />
        {Array.from({ length: unitCount }).map((_, i) => (
          <Column
            key={`car_${i}`}
            title={`CAR ${i}`}
            dataIndex={`car_${i}`}
            render={(text, record: KeysAdapterOffsetsTableInterface) => (
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                size="small"
                precision={0}
                value={record[`car_${i}`]}
                onChange={(e) => {
                  onChangeInputNumber(e, record.id, `car_${i}`);
                }}
              />
            )}
          />
        ))}
        {/** DELETE */}
        <Column
          title="Delete"
          dataIndex="delete"
          key="delete"
          render={(text, record: KeysAdapterOffsetsTableInterface) => (
            <Button
              onClick={() => {
                onClickDelete(record);
              }}
              shape="circle"
              className={!record.isValid ? "" : "buttonStyle__26"}
              disabled={!record.isValid || record.methodType === "POST"}
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          )}
        />
      </Table>
      <GlobalAlert2
        isError={isErrorPutAdapter}
        isSuccess={isSuccessPutAdapter}
        requestType="PUT"
        error={errorPutAdapter}
        name="Adapter"
      />
      <Modal
        title={t("general.confirmSave")}
        open={openModalConfirmSave}
        onOk={() => {
          onClickSave();
          setOpenModalConfirmSave(false);
        }}
        onCancel={() => {
          setOpenModalConfirmSave(false);
        }}
        width="75%"
        bodyStyle={{ overflowX: "auto" }}
      >
        <KeysConfirmSaveTableData data={data} componentType="offsets" />
      </Modal>
    </div>
  );
}
